import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { getApiUrl } from '../utils/config';
import ExportButton from './ExportButton';
import { exportData, exportRenderedTableData } from '../utils/exportUtils';
import { normalizeUser } from '../utils/userUtils';
import LoadingSpinner from './LoadingSpinner';
import '../styles/AdminResourceCenter.css';

// Rank options are loaded from db.ranks (see fetchRanks) rather than hardcoded.

const modalBackdropStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.75)',
  backdropFilter: 'blur(12px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  padding: '1rem',
  animation: 'modalFadeIn 0.3s ease-out',
};

const modalStyle = {
  background: 'white',
  borderRadius: '24px',
  maxWidth: '1000px',
  maxHeight: '95vh',
  width: '90%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 25px 60px -12px rgba(0, 0, 0, 0.4)',
  overflow: 'hidden',
  animation: 'modalSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
};

const metadataModalStyle = {
  background: 'white',
  borderRadius: '24px',
  maxWidth: '1100px',
  maxHeight: '95vh',
  width: '90%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 25px 60px -12px rgba(0, 0, 0, 0.4)',
  overflow: 'hidden',
  animation: 'modalSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
};

const modernInput = {
  border: '2px solid #e2e8f0',
  borderRadius: 12,
  padding: '1rem 1.25rem',
  fontSize: 15,
  fontFamily: 'Inter, sans-serif',
  marginBottom: 12,
  width: '100%',
  background: '#fff',
  transition: 'all 0.3s ease',
  outline: 'none',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
};

const modernTextArea = {
  border: '2px solid #e2e8f0',
  borderRadius: 12,
  padding: '1rem 1.25rem',
  fontSize: 15,
  fontFamily: 'Inter, sans-serif',
  marginBottom: 12,
  width: '100%',
  background: '#fff',
  transition: 'all 0.3s ease',
  outline: 'none',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
  resize: 'vertical',
  minHeight: '120px',
};

const modernButton = {
  background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 12,
  padding: '1rem 2.5rem',
  fontWeight: 600,
  fontSize: 16,
  letterSpacing: 0.5,
  cursor: 'pointer',
  boxShadow: '0 4px 15px rgba(43, 70, 99, 0.4)',
  transition: 'all 0.3s ease',
  outline: 'none',
  position: 'relative',
  overflow: 'hidden',
};

const modernButtonSecondary = {
  background: '#fff',
  color: '#2b4663',
  border: '2px solid #2b4663',
  borderRadius: 12,
  padding: '1rem 2.5rem',
  fontWeight: 600,
  fontSize: 16,
  letterSpacing: 0.5,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  outline: 'none',
  boxShadow: '0 2px 8px rgba(43, 70, 99, 0.1)',
};

const downloadButton = {
  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '0.6rem 1.2rem',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  outline: 'none',
  boxShadow: '0 3px 10px rgba(255, 107, 107, 0.3)',
};

const urlButton = {
  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '0.6rem 1.2rem',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  outline: 'none',
  boxShadow: '0 3px 10px rgba(79, 172, 254, 0.3)',
};

const viewDetailsButton = {
  background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  color: '#2d3748',
  border: 'none',
  borderRadius: 8,
  padding: '0.6rem 1.2rem',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  outline: 'none',
  boxShadow: '0 3px 10px rgba(168, 237, 234, 0.3)',
};

const multiSelectStyle = {
  border: '1.5px solid #bfc8d2',
  borderRadius: 8,
  padding: '0.7rem 1rem',
  fontSize: 15,
  fontFamily: 'Inter, sans-serif',
  marginBottom: 8,
  width: '100%',
  background: '#f4f4f4',
  transition: 'border 0.2s',
  minHeight: '120px',
};

const selectedTagsStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  marginTop: 8,
};

const tagStyle = {
  background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
  color: '#fff',
  padding: '0.3rem 0.8rem',
  borderRadius: 16,
  fontSize: 12,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  boxShadow: '0 2px 8px rgba(43, 70, 99, 0.3)',
};

const removeTagButton = {
  background: 'none',
  border: 'none',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 'bold',
  padding: 0,
  margin: 0,
};

const modernCheckbox = {
  appearance: 'none',
  width: '20px',
  height: '20px',
  border: '2px solid #2b4663',
  borderRadius: '4px',
  backgroundColor: '#fff',
  cursor: 'pointer',
  position: 'relative',
  transition: 'all 0.2s ease',
  margin: 0,
  padding: 0,
};

const modernCheckboxChecked = {
  ...modernCheckbox,
  backgroundColor: '#2b4663',
  borderColor: '#2b4663',
};

const checkboxLabel = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  fontWeight: 500,
  color: '#2b4663',
  cursor: 'pointer',
  fontSize: '15px',
};

const checkboxContainer = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: '12px 16px',
  background: '#f8f9fa',
  borderRadius: '8px',
  border: '1px solid #e9ecef',
};

const whiteContainerStyle = {
  border: '1px solid rgba(193, 212, 233, 0.38)',
  borderRadius: '22px',
  background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(244, 248, 253, 0.98) 100%)',
  boxShadow: '0 20px 42px rgba(6, 20, 35, 0.24)',
  padding: '1.5rem',
  paddingLeft: '1.5rem',
  maxWidth: '1400px',
  margin: '0 auto',
  boxSizing: 'border-box'
};

const AdminResourceCenter = () => {
  // Upload modal state
  const [showModal, setShowModal] = useState(false);
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [textContent, setTextContent] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [targetAudience, setTargetAudience] = useState("student");
  const [rank, setRank] = useState("");
  const [uploadSpecialityTags, setUploadSpecialityTags] = useState([]);
  const [uploadInstitutionTags, setUploadInstitutionTags] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");

  // Metadata modal state
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [uploaderDetails, setUploaderDetails] = useState(null);
  const [loadingUploaderDetails, setLoadingUploaderDetails] = useState(false);
  
  // Edit mode state for metadata modal
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    url: '',
    text: '',
    target_audience: '',
    rank: '',
    specialityTags: [],
    institutionTags: []
  });
  const [editFile, setEditFile] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Resource list state
  const [viewAs, setViewAs] = useState("ALL");
  const [viewRank, setViewRank] = useState("ALL");
  const [resources, setResources] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedSpecialities, setSelectedSpecialities] = useState([]);
  const [selectedInstitutions, setSelectedInstitutions] = useState([]);
  const [includeGeneralResources, setIncludeGeneralResources] = useState(true);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(['name', 'file', 'url', 'audience', 'rank']);
  const columnSelectorRef = useRef(null);

  // Available options state
  const [availableSpecialities, setAvailableSpecialities] = useState([]);
  const [availableInstitutions, setAvailableInstitutions] = useState([]);
  const [availableRanks, setAvailableRanks] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const RESOURCE_COLUMNS = [
    { key: 'name', label: 'Name' },
    { key: 'file', label: 'File' },
    { key: 'url', label: 'URL' },
    { key: 'text', label: 'Text' },
    { key: 'audience', label: 'Audience' },
    { key: 'rank', label: 'Rank' },
    { key: 'speciality', label: 'Speciality' },
    { key: 'institution', label: 'Institution' }
  ];

  // Fetch available specialities and institutions
  const fetchOptions = async () => {
    setLoadingOptions(true);
    try {
      const [specialitiesRes, institutionsRes] = await Promise.all([
        axiosInstance.get("/crud/get_all_specialities"),
        axiosInstance.get("/crud/get_all_institutions")
      ]);
      
      // Handle both object and string formats for specialities
      const specialities = specialitiesRes.data || [];
      const processedSpecialities = specialities.map(item => {
        if (typeof item === 'string') {
          return item;
        } else if (item && typeof item === 'object') {
          return item.speciality || item.name || item._id || JSON.stringify(item);
        }
        return String(item);
      });
      setAvailableSpecialities(processedSpecialities);
      
      // Handle both object and string formats for institutions
      const institutions = institutionsRes.data || [];
      const processedInstitutions = institutions.map(item => {
        if (typeof item === 'string') {
          return item;
        } else if (item && typeof item === 'object') {
          return item.institution || item.name || item._id || JSON.stringify(item);
        }
        return String(item);
      });
      setAvailableInstitutions(processedInstitutions);

      const ranksRes = await axiosInstance.get("/ranks/ranks");
      setAvailableRanks(Array.isArray(ranksRes.data) ? ranksRes.data.map(r => r.name).filter(Boolean) : []);
    } catch (err) {
      console.error("Failed to fetch options:", err);
    } finally {
      setLoadingOptions(false);
    }
  };

  // Fetch uploader details
  const fetchUploaderDetails = async (resource) => {
    if (!resource.uploader_id || !resource.uploader_role) {
      setUploaderDetails(null);
      return;
    }

    setLoadingUploaderDetails(true);
    try {
      let endpoint = "";
      if (resource.uploader_role === "preceptor") {
        endpoint = `/crud/get_preceptor_data/${resource.uploader_id}`;
      } else if (resource.uploader_role === "admin") {
        endpoint = `/crud/get_standalone_admin_data/${resource.uploader_id}`;
      } else {
        setUploaderDetails(null);
        return;
      }

      const response = await axiosInstance.get(endpoint);
      setUploaderDetails(normalizeUser(response.data || {}));
    } catch (err) {
      console.error("Failed to fetch uploader details:", err);
      setUploaderDetails(null);
    } finally {
      setLoadingUploaderDetails(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  useEffect(() => {
    const closeOnOutside = (event) => {
      if (columnSelectorRef.current && !columnSelectorRef.current.contains(event.target)) {
        setShowColumnSelector(false);
      }
    };
    document.addEventListener('mousedown', closeOnOutside);
    return () => document.removeEventListener('mousedown', closeOnOutside);
  }, []);

  // Fetch resources
  const fetchResources = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        page,
        page_size: pageSize,
        include_general_resources: includeGeneralResources,
      };
      
      // Only add view_as_role if not "ALL"
      if (viewAs !== "ALL") {
        params.view_as_role = viewAs;
      }
      
      // Only add view_as_rank if not "ALL" and viewAs is not "ALL"
      if (viewRank !== "ALL" && viewAs !== "ALL") {
        params.view_as_rank = viewRank;
      }
      
      if (selectedSpecialities.length > 0) params.specialityTags = selectedSpecialities;
      if (selectedInstitutions.length > 0) params.institutionTags = selectedInstitutions;
      const res = await axiosInstance.get("/crud/resources/", { params });
      setResources(res.data.resources);
      setTotalPages(res.data.pages);
    } catch (err) {
      setError("Failed to load resources.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResources();
    // eslint-disable-next-line
  }, [page, viewAs, viewRank, includeGeneralResources, selectedSpecialities, selectedInstitutions]);

  // Upload resource
  const handleUpload = async (e) => {
    e.preventDefault();
    
    // Validation
    const validationErrors = [];
    
    // Check if at least one of file, URL, or text content is provided
    const hasFile = file !== null;
    const hasUrl = url && url.trim() !== "";
    const hasText = textContent && textContent.trim() !== "";
    
    if (!hasFile && !hasUrl && !hasText) {
      validationErrors.push("Please provide at least one of: a file, URL, or text content.");
    }
    
    // Check if name is provided
    if (!name || name.trim() === "") {
      validationErrors.push("Please provide a name for the resource.");
    }
    
    // Check if description is provided
    if (!description || description.trim() === "") {
      validationErrors.push("Please provide a description for the resource.");
    }
    
    // Check if target audience is selected
    if (!targetAudience) {
      validationErrors.push("Please select a target audience.");
    }
    
    // Check if rank is selected when target audience is preceptor
    if (targetAudience === "preceptor" && !rank) {
      validationErrors.push("Please select a rank for preceptor resources.");
    }
    
    // URL validation
    if (hasUrl) {
      try {
        new URL(url);
      } catch {
        validationErrors.push("Please enter a valid URL (including http:// or https://).");
      }
    }
    
    // Check mutual exclusivity of speciality and institution tags
    if (uploadSpecialityTags.length > 0 && uploadInstitutionTags.length > 0) {
      validationErrors.push("You can only select either speciality tags OR institution tags, not both.");
    }
    
    // If there are validation errors, show them and stop
    if (validationErrors.length > 0) {
      setUploadError(validationErrors.join(" "));
      setTimeout(() => {
        setUploadError("");
      }, 6000);
      return;
    }
    
    setUploading(true);
    setUploadError("");
    setUploadSuccess("");
    
    const formData = new FormData();
    
    // Only append file if it exists
    if (hasFile) {
      formData.append("file", file);
    }
    
    // Only append URL if it exists
    if (hasUrl) {
      formData.append("url", url.trim());
    }
    
    // Only append text content if it exists
    if (hasText) {
      formData.append("text", textContent.trim());
    }
    
    formData.append("name", name.trim());
    formData.append("description", description.trim());
    formData.append("target_audience", targetAudience);
    
    // Only append rank if it's selected
    if (rank) {
      formData.append("rank", rank);
    }
    
    // Handle speciality tags - only if institution tags are empty
    if (uploadSpecialityTags.length > 0 && uploadInstitutionTags.length === 0) {
      uploadSpecialityTags.forEach(tag => {
        formData.append("specialityTags", tag);
      });
    }
    
    // Handle institution tags - only if speciality tags are empty
    if (uploadInstitutionTags.length > 0 && uploadSpecialityTags.length === 0) {
      uploadInstitutionTags.forEach(tag => {
        formData.append("institutionTags", tag);
      });
    }
    
    try {
      const response = await axiosInstance.post("/crud/upload_resource/", formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setUploadSuccess("Resource uploaded successfully!");
      
      // Reset form
      setFile(null);
      setUrl("");
      setTextContent("");
      setName("");
      setDescription("");
      setRank("");
      setUploadSpecialityTags([]);
      setUploadInstitutionTags([]);
      setShowModal(false);
      fetchResources();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setUploadSuccess("");
      }, 3000);
    } catch (err) {
      console.error("Upload error:", err);
      console.error("Error response:", err.response);
      setUploadError(err?.response?.data?.detail || err?.response?.data?.message || "Upload failed. Please try again.");
      setTimeout(() => {
        setUploadError("");
      }, 6000);
    } finally {
      setUploading(false);
    }
  };

  // Download resource
  const handleDownload = async (resource) => {
    try {
      const response = await axiosInstance.get(`/crud/resources/${resource._id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', resource.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Download failed. Please try again.');
    }
  };

  // Handle resource row click
  const handleResourceClick = async (resource) => {
    setSelectedResource(resource);
    setShowMetadataModal(true);
    setIsEditMode(false); // Reset edit mode when opening modal
    setUploaderDetails(null); // Reset uploader details
    
    // Initialize edit form data with current resource values
    setEditFormData({
      name: resource.name || resource.filename || '',
      description: resource.description || '',
      url: resource.url || '',
      text: resource.text || '',
      target_audience: resource.target_audience || '',
      rank: resource.rank || '',
      specialityTags: resource.specialityTags || [],
      institutionTags: resource.institutionTags || []
    });
    setEditFile(null);
    
    await fetchUploaderDetails(resource);
  };

  // Toggle edit mode
  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
    if (!isEditMode && selectedResource) {
      // Reset form data when entering edit mode
      setEditFormData({
        name: selectedResource.name || selectedResource.filename || '',
        description: selectedResource.description || '',
        url: selectedResource.url || '',
        text: selectedResource.text || '',
        target_audience: selectedResource.target_audience || '',
        rank: selectedResource.rank || '',
        specialityTags: selectedResource.specialityTags || [],
        institutionTags: selectedResource.institutionTags || []
      });
      setEditFile(null);
    }
  };

  // Handle edit form field changes
  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle edit speciality tag changes
  const handleEditSpecialityChange = (e) => {
    const value = e.target.value;
    if (value && !editFormData.specialityTags.includes(value)) {
      setEditFormData(prev => ({
        ...prev,
        specialityTags: [...prev.specialityTags, value],
        institutionTags: [] // Clear institution tags when adding speciality
      }));
    }
  };

  // Handle edit institution tag changes
  const handleEditInstitutionChange = (e) => {
    const value = e.target.value;
    if (value && !editFormData.institutionTags.includes(value)) {
      setEditFormData(prev => ({
        ...prev,
        institutionTags: [...prev.institutionTags, value],
        specialityTags: [] // Clear speciality tags when adding institution
      }));
    }
  };

  // Remove edit speciality tag
  const removeEditSpeciality = (specialityToRemove) => {
    setEditFormData(prev => ({
      ...prev,
      specialityTags: prev.specialityTags.filter(s => s !== specialityToRemove)
    }));
  };

  // Remove edit institution tag
  const removeEditInstitution = (institutionToRemove) => {
    setEditFormData(prev => ({
      ...prev,
      institutionTags: prev.institutionTags.filter(i => i !== institutionToRemove)
    }));
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    if (!selectedResource) return;

    // Validation
    if (!editFormData.name || editFormData.name.trim() === "") {
      alert("Please provide a name for the resource.");
      return;
    }

    if (!editFormData.description || editFormData.description.trim() === "") {
      alert("Please provide a description for the resource.");
      return;
    }

    if (!editFormData.target_audience) {
      alert("Please select a target audience.");
      return;
    }

    if (editFormData.target_audience === "preceptor" && !editFormData.rank) {
      alert("Please select a rank for preceptor resources.");
      return;
    }

    // URL validation if provided
    if (editFormData.url && editFormData.url.trim() !== "") {
      try {
        new URL(editFormData.url);
      } catch {
        alert("Please enter a valid URL (including http:// or https://).");
        return;
      }
    }

    // Check mutual exclusivity of speciality and institution tags
    if (editFormData.specialityTags.length > 0 && editFormData.institutionTags.length > 0) {
      alert("You can only select either speciality tags OR institution tags, not both.");
      return;
    }

    setIsUpdating(true);

    try {
      const formData = new FormData();
      
      // Add form fields
      formData.append('name', editFormData.name.trim());
      formData.append('description', editFormData.description.trim());
      formData.append('target_audience', editFormData.target_audience);
      
      if (editFormData.rank) {
        formData.append('rank', editFormData.rank);
      }
      
      if (editFormData.url && editFormData.url.trim() !== "") {
        formData.append('url', editFormData.url.trim());
      }
      
      if (editFormData.text && editFormData.text.trim() !== "") {
        formData.append('text', editFormData.text.trim());
      }
      
      if (editFile) {
        formData.append('file', editFile);
      }
      
      // Add tags
      if (editFormData.specialityTags.length > 0) {
        editFormData.specialityTags.forEach(tag => {
          formData.append('specialityTags', tag);
        });
      }
      
      if (editFormData.institutionTags.length > 0) {
        editFormData.institutionTags.forEach(tag => {
          formData.append('institutionTags', tag);
        });
      }

      const response = await fetch(getApiUrl(`api/crud/resources/${selectedResource._id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (response.ok) {
        const updatedResource = await response.json();
        
        // Update the resource in the list
        setResources(prev => prev.map(r => 
          r._id === selectedResource._id ? updatedResource : r
        ));
        
        // Update selected resource
        setSelectedResource(updatedResource);
        
        // Exit edit mode
        setIsEditMode(false);
        
        alert('Resource updated successfully!');
        
        // Refresh the resources list to get the latest data
        fetchResources();
      } else {
        const errorData = await response.json();
        alert(`Error updating resource: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating resource:', error);
      alert('Error updating resource. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle multi-select changes
  const handleSpecialityChange = (e) => {
    const value = e.target.value;
    if (value && !selectedSpecialities.includes(value)) {
      setSelectedSpecialities([...selectedSpecialities, value]);
    }
  };

  const handleInstitutionChange = (e) => {
    const value = e.target.value;
    if (value && !selectedInstitutions.includes(value)) {
      setSelectedInstitutions([...selectedInstitutions, value]);
    }
  };

  const removeSpeciality = (speciality) => {
    setSelectedSpecialities(selectedSpecialities.filter(s => s !== speciality));
  };

  const removeInstitution = (institution) => {
    setSelectedInstitutions(selectedInstitutions.filter(i => i !== institution));
  };

  // Upload tag handling functions
  const handleUploadSpecialityChange = (e) => {
    const value = e.target.value;
    if (value && !uploadSpecialityTags.includes(value)) {
      // Clear institution tags if speciality tags are being added
      setUploadInstitutionTags([]);
      setUploadSpecialityTags([...uploadSpecialityTags, value]);
    }
  };

  const handleUploadInstitutionChange = (e) => {
    const value = e.target.value;
    if (value && !uploadInstitutionTags.includes(value)) {
      // Clear speciality tags if institution tags are being added
      setUploadSpecialityTags([]);
      setUploadInstitutionTags([...uploadInstitutionTags, value]);
    }
  };

  const removeUploadSpeciality = (speciality) => {
    setUploadSpecialityTags(uploadSpecialityTags.filter(s => s !== speciality));
  };

  const removeUploadInstitution = (institution) => {
    setUploadInstitutionTags(uploadInstitutionTags.filter(i => i !== institution));
  };

  // Pagination
  const handlePrev = () => setPage((p) => Math.max(1, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages === 0 ? 1 : totalPages, p + 1));

  // Filter reload
  const handleReload = () => {
    setPage(1);
    fetchResources();
  };

  // Clear filters
  const clearFilters = () => {
    setViewAs("ALL");
    setViewRank("ALL");
    setSelectedSpecialities([]);
    setSelectedInstitutions([]);
    setPage(1);
  };

  // Get uploader display name
  const getUploaderDisplayName = (resource) => {
    if (uploaderDetails) {
      const firstName = uploaderDetails.firstname || '';
      const lastName = uploaderDetails.lastname || '';
      return `${firstName} ${lastName}`.trim() || 'N/A';
    }
    return 'N/A';
  };

  // Get uploader role display
  const getUploaderRoleDisplay = (resource) => {
    if (resource.uploader_role === "preceptor") {
      return "Preceptor, Admin";
    }
    return resource.uploader_role || 'N/A';
  };

  // Handle export functionality
  const handleExport = (format) => {
    try {
      // Export exactly what's currently displayed in the table
      const renderedResources = resources.map(resource => ({
        name: resource.name || resource.filename || 'N/A',
        file: resource.file_id ? 'Available' : 'N/A',
        url: resource.url ? 'Available' : 'N/A',
        text: resource.text ? 'Available' : 'N/A',
        target_audience: resource.target_audience || 'N/A',
        rank: resource.rank || 'N/A',
        specialityTags: Array.isArray(resource.specialityTags) && resource.specialityTags.length > 0 
          ? resource.specialityTags.join(', ') 
          : 'None',
        institutionTags: Array.isArray(resource.institutionTags) && resource.institutionTags.length > 0 
          ? resource.institutionTags.join(', ') 
          : 'None'
      }));

      const columns = [
        { key: 'name', label: 'Name' },
        { key: 'file', label: 'File' },
        { key: 'url', label: 'URL' },
        { key: 'text', label: 'Text' },
        { key: 'target_audience', label: 'Audience' },
        { key: 'rank', label: 'Rank' },
        { key: 'specialityTags', label: 'Speciality' },
        { key: 'institutionTags', label: 'Institution' }
      ];

      const title = 'Admin Resource Center - Resources';
      const filename = `admin_resource_center_resources_${new Date().toISOString().split('T')[0]}`;
      
      // Use the new export function for rendered data
      exportRenderedTableData(renderedResources, columns, title, filename, format);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  const renderResourceCell = (resource, columnKey) => {
    switch (columnKey) {
      case 'name':
        return <span style={{ fontWeight: 600, fontSize: 14 }}>{resource.name || resource.filename || 'N/A'}</span>;
      case 'file':
        return resource.file_id ? (
          <a
            href={getApiUrl(`api/resources/download/${encodeURIComponent(resource.filename)}`)}
            style={{ color: '#0f3a63', fontWeight: 600, textDecoration: 'none' }}
            onClick={(e) => e.stopPropagation()}
            target="_blank"
            rel="noopener noreferrer"
            title="Download file"
          >
            Available
          </a>
        ) : <span style={{ color: '#64748b' }}>N/A</span>;
      case 'url':
        return resource.url ? (
          <a
            href={resource.url}
            style={{ color: '#0f3a63', fontWeight: 600, textDecoration: 'none' }}
            onClick={(e) => e.stopPropagation()}
            target="_blank"
            rel="noopener noreferrer"
            title="Open URL"
          >
            Available
          </a>
        ) : <span style={{ color: '#64748b' }}>N/A</span>;
      case 'text':
        return resource.text ? <span style={{ color: '#111827', fontWeight: 600 }}>Available</span> : <span style={{ color: '#64748b' }}>N/A</span>;
      case 'audience':
        return <span style={{ textTransform: 'capitalize' }}>{resource.target_audience || 'N/A'}</span>;
      case 'rank':
        return <span style={{ textTransform: 'uppercase' }}>{resource.rank || 'N/A'}</span>;
      case 'speciality':
        return (resource.specialityTags || []).length > 0 ? resource.specialityTags.join(', ') : 'None';
      case 'institution':
        return (resource.institutionTags || []).length > 0 ? resource.institutionTags.join(', ') : 'None';
      default:
        return 'N/A';
    }
  };

  return (
    <div style={{ 
      ...whiteContainerStyle,
      padding: 'clamp(1rem, 2.5vw, 2rem)',
      minHeight: '100vh',
      width: 'min(1400px, 100%)',
      margin: '0 auto',
      overflow: 'hidden',
      background: '#fff'
    }}>
      <style>
        {`
          @keyframes modalFadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          
          @keyframes modalSlideIn {
            from {
              transform: translateY(-50px) scale(0.95);
              opacity: 0;
            }
            to {
              transform: translateY(0) scale(1);
              opacity: 1;
            }
          }
          
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          
          @keyframes slideInScale {
            from {
              transform: translateY(-30px) scale(0.95);
              opacity: 0;
            }
            to {
              transform: translateY(0) scale(1);
              opacity: 1;
            }
          }
          
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          
          .modern-checkbox:checked::after {
            content: '✓';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            color: white;
            font-size: 14px;
            font-weight: bold;
            line-height: 1;
          }
          
          .modern-input:focus {
            border-color: #2b4663 !important;
            box-shadow: 0 0 0 4px rgba(43, 70, 99, 0.1) !important;
          }
          
          .modern-textarea:focus {
            border-color: #2b4663 !important;
            box-shadow: 0 0 0 4px rgba(43, 70, 99, 0.1) !important;
          }
          
          .modern-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(43, 70, 99, 0.4);
          }
          
          .modern-button:active {
            transform: translateY(0);
          }
          
          .tooltip {
            visibility: hidden;
            position: absolute;
            z-index: 10000;
            background: rgba(0, 0, 0, 0.9);
            color: white;
            padding: 12px 16px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            max-width: 300px;
            line-height: 1.4;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
            opacity: 0;
            transition: all 0.3s ease;
            pointer-events: none;
          }
          
          .tooltip-trigger:hover .tooltip {
            visibility: visible;
            opacity: 1;
          }
          
          .url-button:hover {
            transform: scale(1.05) !important;
            box-shadow: 0 4px 15px rgba(79, 172, 254, 0.4) !important;
          }
          
          .download-button:hover {
            transform: scale(1.05) !important;
            box-shadow: 0 4px 15px rgba(255, 107, 107, 0.4) !important;
          }
        `}
      </style>

      {/* Success Message */}
      {uploadSuccess && (
        <div style={{
          position: 'fixed',
          top: '2rem',
          right: '2rem',
          background: '#10b981',
          color: '#fff',
          padding: '1rem 1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
          zIndex: 1001,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          maxWidth: '400px',
          animation: 'slideIn 0.3s ease-out'
        }}>
          <div style={{ fontSize: '20px' }}>✅</div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Success!</div>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>{uploadSuccess}</div>
          </div>
          <button 
            onClick={() => setUploadSuccess("")}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '18px',
              padding: '4px',
              marginLeft: 'auto'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Error Message */}
      {uploadError && (
        <div style={{
          position: 'fixed',
          top: '2rem',
          right: '2rem',
          background: '#ef4444',
          color: '#fff',
          padding: '1rem 1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
          zIndex: 1001,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          maxWidth: '400px',
          animation: 'slideIn 0.3s ease-out'
        }}>
          <div style={{ fontSize: '20px' }}>✅</div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Error!</div>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>{uploadError}</div>
          </div>
          <button 
            onClick={() => setUploadError("")}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '18px',
              padding: '4px',
              marginLeft: 'auto'
            }}
          >
            ×
          </button>
        </div>
      )}

      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 32,
        background: 'linear-gradient(140deg, rgba(10, 30, 52, 0.92) 0%, rgba(21, 45, 72, 0.84) 100%)',
        padding: '1.35rem 1.4rem',
        borderRadius: 20,
        border: '1px solid rgba(193, 212, 233, 0.45)',
        boxShadow: '0 14px 34px rgba(22, 54, 86, 0.2)',
        color: '#fff'
      }}>
        <div>
          <h2 style={{ 
            margin: 0, 
            fontWeight: 800, 
            letterSpacing: 1.2,
            fontSize: 28,
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            Admin Resource Center
          </h2>
          <p style={{ 
            margin: '8px 0 0 0', 
            opacity: 0.9, 
            fontSize: 16,
            fontWeight: 400
          }}>
            Manage and organize educational resources for students and preceptors
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button 
            style={{
              ...modernButton,
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: '#fff',
              boxShadow: '0 4px 15px rgba(255, 255, 255, 0.2)'
            }} 
            onClick={() => setShowModal((prev) => !prev)}
          >
            {showModal ? 'Hide Create Resource' : 'Create Resource'}
          </button>
        </div>
      </div>

      {/* Inline Create Resource Panel */}
      {showModal && (
        <div style={{
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(244, 248, 253, 0.98) 100%)',
          border: '1px solid rgba(193, 212, 233, 0.75)',
          borderRadius: '24px',
          boxShadow: '0 20px 42px rgba(6, 20, 35, 0.18)',
          marginBottom: '2rem',
          overflow: 'hidden'
        }}>
            {/* Header with dark blue gradient */}
            <div style={{
              background: 'linear-gradient(140deg, rgba(10, 30, 52, 0.92) 0%, rgba(21, 45, 72, 0.84) 100%)',
              padding: '2.5rem',
              color: 'white',
              position: 'relative'
            }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  position: 'absolute',
                  top: '1.5rem',
                  right: '1.5rem',
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.9)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  transition: 'all 0.3s ease',
                  fontSize: '20px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'none';
                  e.target.style.color = 'rgba(255, 255, 255, 0.9)';
                }}
              >
                ×
              </button>
              
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📚</div>
                <h2 style={{ 
                  margin: 0, 
                  fontSize: '2rem', 
                  color: '#ffffff',
                  fontWeight: 700,
                  marginBottom: '0.5rem'
                }}>
                  Create New Resource
                </h2>
                <p style={{ 
                  margin: 0, 
                  fontSize: '1.1rem', 
                  opacity: 0.9,
                  fontWeight: 400
                }}>
                  Share knowledge and resources with students and preceptors
                </p>
              </div>
            </div>

            {/* Content */}
            <div style={{ 
              padding: '2.5rem',
              flex: 1,
              overflowY: 'auto'
            }}>
              <form onSubmit={handleUpload} style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                {/* Basic Information Section */}
                <div style={{
                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                  borderRadius: '16px',
                  padding: '2rem',
                  border: '2px solid rgba(43, 70, 99, 0.08)'
                }}>
                  <h3 style={{ 
                    margin: '0 0 1.5rem 0', 
                    color: '#1e293b', 
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    Basic Information
                  </h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
                    <div>
                      <label style={{ 
                        fontWeight: 600, 
                        color: '#1e293b', 
                        display: 'block', 
                        marginBottom: '8px',
                        fontSize: '15px'
                      }}>
                        Resource Name <span style={{color: '#ef4444'}}>*</span>
                      </label>
                      <input 
                        type="text" 
                        placeholder="Enter a descriptive name for your resource..." 
                        value={name} 
                        onChange={e => setName(e.target.value)} 
                        style={{
                          ...modernInput,
                          background: 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          fontSize: '15px',
                          fontWeight: 500
                        }}
                        required
                      />
                    </div>
                    
                    <div>
                      <label style={{ 
                        fontWeight: 600, 
                        color: '#1e293b', 
                        display: 'block', 
                        marginBottom: '8px',
                        fontSize: '15px'
                      }}>
                        Description <span style={{color: '#ef4444'}}>*</span>
                      </label>
                      <textarea 
                        placeholder="Provide a detailed description of the resource and its purpose..." 
                        value={description} 
                        onChange={e => setDescription(e.target.value)} 
                        style={{
                          ...modernTextArea,
                          background: 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          fontSize: '15px',
                          fontWeight: 500,
                          minHeight: '120px',
                          resize: 'vertical'
                        }}
                        rows={4}
                        required
                      />
                    </div>
                  </div>
                </div>
                
                {/* Resource Content Section */}
                <div style={{
                  background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                  borderRadius: '16px',
                  padding: '2rem',
                  border: '2px solid rgba(59, 130, 246, 0.15)'
                }}>
                  <h3 style={{ 
                    margin: '0 0 1rem 0', 
                    color: '#1e293b', 
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    Resource Content
                  </h3>
                  <p style={{
                    margin: '0 0 1.5rem 0',
                    color: '#64748b',
                    fontSize: '14px',
                    fontStyle: 'italic'
                  }}>
                    Provide at least one form of content: file, URL, or text
                  </p>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
                    {/* File Upload */}
                    <div>
                      <label style={{ 
                        fontWeight: 600, 
                        color: '#1e293b', 
                        display: 'block', 
                        marginBottom: '8px',
                        fontSize: '15px'
                      }}>
                        Upload File
                      </label>
                      <input 
                        type="file" 
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          background: 'white',
                          fontSize: '15px',
                          cursor: 'pointer'
                        }}
                        onChange={e => setFile(e.target.files[0])} 
                      />
                    </div>
                    
                    {/* URL Input */}
                    <div>
                      <label style={{ 
                        fontWeight: 600, 
                        color: '#1e293b', 
                        display: 'block', 
                        marginBottom: '8px',
                        fontSize: '15px'
                      }}>
                        Resource URL
                      </label>
                      <input 
                        type="url" 
                        placeholder="https://example.com/resource" 
                        value={url} 
                        onChange={e => setUrl(e.target.value)} 
                        style={{
                          ...modernInput,
                          background: 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          fontSize: '15px',
                          fontWeight: 500
                        }}
                      />
                    </div>
                    
                    {/* Text Content */}
                    <div>
                      <label style={{ 
                        fontWeight: 600, 
                        color: '#1e293b', 
                        display: 'block', 
                        marginBottom: '8px',
                        fontSize: '15px'
                      }}>
                        Text Content
                      </label>
                      <textarea 
                        placeholder="Enter text content, notes, or instructions here..." 
                        value={textContent} 
                        onChange={e => setTextContent(e.target.value)} 
                        style={{
                          ...modernTextArea,
                          background: 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          fontSize: '15px',
                          fontWeight: 500,
                          minHeight: '100px',
                          resize: 'vertical'
                        }}
                        rows={4}
                      />
                    </div>
                  </div>
                </div>

                {/* Targeting Section */}
                <div style={{
                  background: 'linear-gradient(135deg, #fef7ff 0%, #faf5ff 100%)',
                  borderRadius: '16px',
                  padding: '2rem',
                  border: '2px solid rgba(147, 51, 234, 0.15)'
                }}>
                  <h3 style={{ 
                    margin: '0 0 1.5rem 0', 
                    color: '#1e293b', 
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    Targeting & Access
                  </h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                    <div>
                      <label style={{ 
                        fontWeight: 600, 
                        color: '#1e293b', 
                        display: 'block', 
                        marginBottom: '8px',
                        fontSize: '15px'
                      }}>
                        Target Audience <span style={{color: '#ef4444'}}>*</span>
                      </label>
                      <select 
                        value={targetAudience} 
                        onChange={e => setTargetAudience(e.target.value)} 
                        style={{
                          ...modernInput,
                          background: 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          fontSize: '15px',
                          fontWeight: 500,
                          cursor: 'pointer'
                        }}
                        required
                      >
                        <option value="student">Students</option>
                        <option value="preceptor">Preceptors</option>
                      </select>
                    </div>
                    
                    <div>
                      <label style={{ 
                        fontWeight: 600, 
                        color: '#1e293b', 
                        display: 'block', 
                        marginBottom: '8px',
                        fontSize: '15px'
                      }}>
                        Rank {targetAudience === "preceptor" && <span style={{color: '#ef4444'}}>*</span>}
                      </label>
                      <select 
                        value={rank} 
                        onChange={e => setRank(e.target.value)} 
                        style={{
                          ...modernInput,
                          background: 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          fontSize: '15px',
                          fontWeight: 500,
                          cursor: 'pointer'
                        }}
                        required={targetAudience === "preceptor"}
                      >
                        <option value="">Select rank...</option>
                        {availableRanks.map(r => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Tags Section */}
                <div style={{
                  background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
                  borderRadius: '16px',
                  padding: '2rem',
                  border: '2px solid rgba(34, 197, 94, 0.15)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '1rem'
                  }}>
                    <h3 style={{ 
                      margin: 0, 
                      color: '#1e293b', 
                      fontWeight: 700,
                      fontSize: '1.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      Tags & Categories
                    </h3>
                    <div 
                      className="tooltip-trigger"
                      style={{ position: 'relative' }}
                      onMouseEnter={(e) => {
                        const tooltip = e.currentTarget.querySelector('.tooltip');
                        if (tooltip) {
                          tooltip.style.visibility = 'visible';
                          tooltip.style.opacity = '1';
                          tooltip.style.bottom = '100%';
                          tooltip.style.left = '50%';
                          tooltip.style.transform = 'translateX(-50%)';
                          tooltip.style.marginBottom = '8px';
                        }
                      }}
                      onMouseLeave={(e) => {
                        const tooltip = e.currentTarget.querySelector('.tooltip');
                        if (tooltip) {
                          tooltip.style.visibility = 'hidden';
                          tooltip.style.opacity = '0';
                        }
                      }}
                    >
                      <button 
                        type="button"
                        style={{
                          background: 'rgba(59, 130, 246, 0.1)',
                          color: '#3b82f6',
                          border: '1px solid rgba(59, 130, 246, 0.2)',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}
                      >
                        i
                      </button>
                        <div className="tooltip">
                        Leave both empty to create general resources available to everyone. Select either speciality OR institution tags, not both.
                      </div>
                    </div>
                  </div>
                  <p style={{
                    margin: '0 0 1.5rem 0',
                    color: '#64748b',
                    fontSize: '14px',
                    fontStyle: 'italic'
                  }}>
                    Optional: Select either specialities OR institutions to target specific groups
                  </p>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                    {/* Speciality Tags */}
                    <div>
                      <label style={{ 
                        fontWeight: 600, 
                        color: '#1e293b', 
                        display: 'block', 
                        marginBottom: '8px',
                        fontSize: '15px'
                      }}>
                        Speciality Tags
                      </label>
                      <select 
                        value="" 
                        onChange={handleUploadSpecialityChange} 
                        style={{
                          ...modernInput,
                          background: 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          fontSize: '15px',
                          fontWeight: 500,
                          cursor: 'pointer'
                        }}
                        disabled={loadingOptions || uploadInstitutionTags.length > 0}
                      >
                        <option value="">Select specialities...</option>
                        {availableSpecialities.map((speciality, index) => (
                          <option key={`upload-speciality-${index}-${speciality}`} value={speciality}>
                            {speciality}
                          </option>
                        ))}
                      </select>
                      {uploadSpecialityTags.length > 0 && (
                        <div style={{...selectedTagsStyle, marginTop: '12px'}}>
                          {uploadSpecialityTags.map((speciality, index) => (
                            <span key={`upload-selected-speciality-${index}-${speciality}`} style={{
                              ...tagStyle,
                              background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                              boxShadow: '0 4px 12px rgba(43, 70, 99, 0.3)',
                              fontSize: '13px',
                              padding: '6px 12px'
                            }}>
                              {speciality}
                              <button 
                                style={removeTagButton}
                                onClick={() => removeUploadSpeciality(speciality)}
                                title="Remove"
                              >
                                ✕
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Institution Tags */}
                    <div>
                      <label style={{ 
                        fontWeight: 600, 
                        color: '#1e293b', 
                        display: 'block', 
                        marginBottom: '8px',
                        fontSize: '15px'
                      }}>
                        Institution Tags
                      </label>
                      <select 
                        value="" 
                        onChange={handleUploadInstitutionChange} 
                        style={{
                          ...modernInput,
                          background: 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          fontSize: '15px',
                          fontWeight: 500,
                          cursor: 'pointer'
                        }}
                        disabled={loadingOptions || uploadSpecialityTags.length > 0}
                      >
                        <option value="">Select institutions...</option>
                        {availableInstitutions.map((institution, index) => (
                          <option key={`upload-institution-${index}-${institution}`} value={institution}>
                            {institution}
                          </option>
                        ))}
                      </select>
                      {uploadInstitutionTags.length > 0 && (
                        <div style={{...selectedTagsStyle, marginTop: '12px'}}>
                          {uploadInstitutionTags.map((institution, index) => (
                            <span key={`upload-selected-institution-${index}-${institution}`} style={{
                              ...tagStyle,
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                              fontSize: '13px',
                              padding: '6px 12px'
                            }}>
                              {institution}
                              <button 
                                type="button"
                                style={removeTagButton}
                                onClick={() => removeUploadInstitution(institution)}
                                title="Remove"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ 
                  display: 'flex', 
                  gap: '1rem', 
                  paddingTop: '1rem',
                  borderTop: '2px solid #f1f5f9',
                  justifyContent: 'flex-end'
                }}>
                  <button 
                    type="button" 
                    style={{
                      ...modernButtonSecondary,
                      padding: '14px 32px',
                      fontSize: '16px',
                      fontWeight: 600,
                      borderRadius: '12px'
                    }} 
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    style={{
                      ...modernButton,
                      padding: '14px 32px',
                      fontSize: '16px',
                      fontWeight: 600,
                      borderRadius: '12px',
                      background: uploading 
                        ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)' 
                        : 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                      cursor: uploading ? 'not-allowed' : 'pointer'
                    }} 
                    disabled={uploading}
                  >
                    {uploading ? (
                      <>
                        <span style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }}>⏳</span>
                        Creating...
                      </>
                    ) : (
                      <>
                        Create Resource
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
        </div>
      )}

      {/* Metadata Modal - Detailed Resource View */}
      {showMetadataModal && selectedResource && (
        <div style={modalBackdropStyle} onClick={() => setShowMetadataModal(false)}>
          <div style={metadataModalStyle} onClick={e => e.stopPropagation()}>
            {/* Header with dark blue gradient */}
            <div style={{
              background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
              padding: '2.5rem',
              color: 'white',
              position: 'relative'
            }}>
              <button
                onClick={() => {
                  setShowMetadataModal(false);
                  setIsEditMode(false);
                }}
                style={{
                  position: 'absolute',
                  top: '1.5rem',
                  right: '1.5rem',
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.9)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  transition: 'all 0.3s ease',
                  fontSize: '20px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'none';
                  e.target.style.color = 'rgba(255, 255, 255, 0.9)';
                }}
              >
                ×
              </button>
              
              {!isEditMode && (
                <button
                  onClick={toggleEditMode}
                  style={{
                    position: 'absolute',
                    top: '1.5rem',
                    right: '4rem',
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    transition: 'all 0.3s ease',
                    fontSize: '14px',
                    fontWeight: 600,
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  }}
                >
                  Edit
                </button>
              )}
              
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
                  {isEditMode ? '✏️' : '📄'}
                </div>
                <h2 style={{ 
                  margin: 0, 
                  fontSize: '2rem', 
                  fontWeight: 700,
                  marginBottom: '0.5rem'
                }}>
                  {isEditMode ? 'Edit Resource' : (selectedResource.name || selectedResource.filename || 'Resource Details')}
                </h2>
                <p style={{ 
                  margin: 0, 
                  fontSize: '1.1rem', 
                  opacity: 0.9,
                  fontWeight: 400
                }}>
                  {isEditMode ? 'Modify resource information and save changes' : 'Complete resource information and metadata'}
                </p>
              </div>
            </div>

            {/* Content */}
            <div style={{ 
              padding: '2.5rem',
              flex: 1,
              overflowY: 'auto'
            }}>
              {/* Basic Information Section */}
              <div style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                borderRadius: '16px',
                padding: '2rem',
                border: '2px solid rgba(43, 70, 99, 0.08)',
                marginBottom: '2rem'
              }}>
                <h3 style={{ 
                  margin: '0 0 1.5rem 0', 
                  color: '#1e293b', 
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  Basic Information
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      Resource Name {isEditMode && <span style={{color: '#ef4444'}}>*</span>}
                    </label>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={editFormData.name}
                        onChange={(e) => handleEditFormChange('name', e.target.value)}
                        style={{
                          background: 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          fontSize: '15px',
                          fontWeight: 500,
                          width: '100%',
                          outline: 'none'
                        }}
                        placeholder="Enter resource name..."
                        required
                      />
                    ) : (
                      <div style={{
                        background: 'white',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: '#374151'
                      }}>
                        {selectedResource.name || selectedResource.filename || 'N/A'}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      Description {isEditMode && <span style={{color: '#ef4444'}}>*</span>}
                    </label>
                    {isEditMode ? (
                      <textarea
                        value={editFormData.description}
                        onChange={(e) => handleEditFormChange('description', e.target.value)}
                        style={{
                          background: 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          fontSize: '15px',
                          fontWeight: 500,
                          width: '100%',
                          minHeight: '120px',
                          resize: 'vertical',
                          outline: 'none'
                        }}
                        placeholder="Enter description..."
                        required
                      />
                    ) : (
                      <div style={{
                        background: 'white',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: '#374151',
                        minHeight: '60px',
                        wordWrap: 'break-word'
                      }}>
                        {selectedResource.description || 'No description provided'}
                      </div>
                    )}
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
                    <div>
                      <label style={{ 
                        fontWeight: 600, 
                        color: '#1e293b', 
                        display: 'block', 
                        marginBottom: '8px',
                        fontSize: '15px'
                      }}>
                        Upload Date
                      </label>
                      <div style={{
                        background: 'white',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: '#374151'
                      }}>
                        {selectedResource.upload_date ? new Date(selectedResource.upload_date).toLocaleDateString() : 'Unknown'}
                      </div>
                    </div>
                    
                    <div>
                      <label style={{ 
                        fontWeight: 600, 
                        color: '#1e293b', 
                        display: 'block', 
                        marginBottom: '8px',
                        fontSize: '15px'
                      }}>
                        Uploader
                    </label>
                      <div style={{
                        background: 'white',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: '#374151'
                      }}>
                        {selectedResource.uploader_name || 'Unknown'}
                      </div>
                    </div>
                    
                    <div>
                      <label style={{ 
                        fontWeight: 600, 
                        color: '#1e293b', 
                        display: 'block', 
                        marginBottom: '8px',
                        fontSize: '15px'
                      }}>
                        Content Type
                      </label>
                      <div style={{
                        background: 'white',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: '#374151'
                      }}>
                        {selectedResource.content_type || 'Unknown'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Resource Content Section */}
              <div style={{
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                borderRadius: '16px',
                padding: '2rem',
                border: '2px solid rgba(59, 130, 246, 0.15)',
                marginBottom: '2rem'
              }}>
                <h3 style={{ 
                  margin: '0 0 1.5rem 0', 
                  color: '#1e293b', 
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  Content & Access
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
                  {/* File Information */}
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      File {isEditMode && '(Upload new file to replace current)'}
                    </label>
                    {isEditMode ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {selectedResource.filename && (
                          <div style={{
                            background: '#f1f5f9',
                            border: '2px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '14px 16px',
                            fontSize: '14px',
                            color: '#64748b'
                          }}>
                            Current file: {selectedResource.filename}
                          </div>
                        )}
                        <input
                          type="file"
                          onChange={(e) => setEditFile(e.target.files[0])}
                          style={{
                            background: 'white',
                            border: '2px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '14px 16px',
                            fontSize: '15px',
                            cursor: 'pointer'
                          }}
                        />
                      </div>
                    ) : (
                      selectedResource.file_id ? (
                        <div style={{
                          background: 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          fontSize: '15px',
                          fontWeight: 500,
                          color: '#374151',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <span>{selectedResource.filename}</span>
                          <a 
                            href={getApiUrl(`api/resources/download/${encodeURIComponent(selectedResource.filename)}`)}
                            style={{
                              background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                              color: 'white',
                              textDecoration: 'none',
                              padding: '6px 12px',
                              borderRadius: '8px',
                              fontSize: '13px',
                              fontWeight: 600,
                              marginLeft: 'auto'
                            }}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Download
                          </a>
                        </div>
                      ) : (
                        <div style={{
                          background: '#f1f5f9',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          fontSize: '15px',
                          color: '#64748b',
                          fontStyle: 'italic'
                        }}>
                          No file attached
                        </div>
                      )
                    )}
                  </div>
                  
                  {/* URL Information */}
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      URL
                    </label>
                    {isEditMode ? (
                      <input
                        type="url"
                        value={editFormData.url}
                        onChange={(e) => handleEditFormChange('url', e.target.value)}
                        style={{
                          background: 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          fontSize: '15px',
                          fontWeight: 500,
                          width: '100%',
                          outline: 'none'
                        }}
                        placeholder="https://example.com/resource"
                      />
                    ) : (
                      selectedResource.url ? (
                        <div style={{
                          background: 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          fontSize: '15px',
                          fontWeight: 500,
                          color: '#374151',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <span style={{ wordBreak: 'break-all', flex: 1 }}>{selectedResource.url}</span>
                          <a 
                            href={selectedResource.url}
                            style={{
                              background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                              color: 'white',
                              textDecoration: 'none',
                              padding: '6px 12px',
                              borderRadius: '8px',
                              fontSize: '13px',
                              fontWeight: 600
                            }}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Visit
                          </a>
                        </div>
                      ) : (
                        <div style={{
                          background: '#f1f5f9',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          fontSize: '15px',
                          color: '#64748b',
                          fontStyle: 'italic'
                        }}>
                          No URL attached
                        </div>
                      )
                    )}
                  </div>
                  
                  {/* Text Content */}
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      Text Content
                    </label>
                    {isEditMode ? (
                      <textarea
                        value={editFormData.text}
                        onChange={(e) => handleEditFormChange('text', e.target.value)}
                        style={{
                          background: 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          fontSize: '15px',
                          fontWeight: 500,
                          width: '100%',
                          minHeight: '120px',
                          resize: 'vertical',
                          outline: 'none'
                        }}
                        placeholder="Enter text content, notes, or instructions..."
                      />
                    ) : (
                      selectedResource.text ? (
                        <div style={{
                          background: 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          fontSize: '15px',
                          fontWeight: 500,
                          color: '#374151',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          whiteSpace: 'pre-wrap',
                          wordWrap: 'break-word'
                        }}>
                          {selectedResource.text}
                        </div>
                      ) : (
                        <div style={{
                          background: '#f1f5f9',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          fontSize: '15px',
                          color: '#64748b',
                          fontStyle: 'italic'
                        }}>
                          No text content
                        </div>
                      )
                    )}
                  </div>
                  
                  {!isEditMode && !selectedResource.file_id && !selectedResource.url && !selectedResource.text && (
                    <div style={{ 
                      background: '#fef2f2', 
                      border: '2px solid #ef4444', 
                      borderRadius: '12px',
                      padding: '1.5rem',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📭</div>
                      <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: '0.5rem' }}>No Content Available</div>
                      <div style={{ fontSize: '14px', color: '#b91c1c' }}>
                        This resource doesn't have any files, URLs, or text content attached.
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Targeting Section */}
              <div style={{
                background: 'linear-gradient(135deg, #fef7ff 0%, #faf5ff 100%)',
                borderRadius: '16px',
                padding: '2rem',
                border: '2px solid rgba(147, 51, 234, 0.15)',
                marginBottom: '2rem'
              }}>
                <h3 style={{ 
                  margin: '0 0 1.5rem 0', 
                  color: '#1e293b', 
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  Targeting & Access
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      Target Audience
                    </label>
                    {isEditMode ? (
                      <select
                        value={editFormData.target_audience}
                        onChange={(e) => handleEditFormChange('target_audience', e.target.value)}
                        style={{
                          background: 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          fontSize: '15px',
                          fontWeight: 500,
                          width: '100%',
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">Select Audience</option>
                        <option value="ALL">All Users</option>
                        <option value="student">Students</option>
                        <option value="preceptor">Preceptors</option>
                        <option value="admin">Administrators</option>
                      </select>
                    ) : (
                      <div style={{
                        background: 'white',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: '#374151'
                      }}>
                        {selectedResource.target_audience?.charAt(0).toUpperCase() + selectedResource.target_audience?.slice(1) || 'Not specified'}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      Rank
                    </label>
                    {isEditMode ? (
                      <select
                        value={editFormData.rank}
                        onChange={(e) => handleEditFormChange('rank', e.target.value)}
                        style={{
                          background: 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '14px 16px',
                          fontSize: '15px',
                          fontWeight: 500,
                          width: '100%',
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">Select Rank</option>
                        <option value="ALL">All Ranks</option>
                        <option value="FIRST">First Year</option>
                        <option value="SECOND">Second Year</option>
                        <option value="THIRD">Third Year</option>
                        <option value="FOURTH">Fourth Year</option>
                        <option value="FIFTH">Fifth Year</option>
                        <option value="SIXTH">Sixth Year</option>
                      </select>
                    ) : (
                      <div style={{
                        background: 'white',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: '#374151'
                      }}>
                        {selectedResource.rank ? (selectedResource.rank === 'ALL' ? 'All Ranks' : 
                         selectedResource.rank === 'FIRST' ? 'First Year' :
                         selectedResource.rank === 'SECOND' ? 'Second Year' :
                         selectedResource.rank === 'THIRD' ? 'Third Year' :
                         selectedResource.rank === 'FOURTH' ? 'Fourth Year' :
                         selectedResource.rank === 'FIFTH' ? 'Fifth Year' :
                         selectedResource.rank === 'SIXTH' ? 'Sixth Year' : selectedResource.rank) : 'Not specified'}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Tags Section */}
              <div style={{
                background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
                borderRadius: '16px',
                padding: '2rem',
                border: '2px solid rgba(34, 197, 94, 0.15)',
                marginBottom: '2rem'
              }}>
                <h3 style={{ 
                  margin: '0 0 1.5rem 0', 
                  color: '#1e293b', 
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  Tags & Categories
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                  {/* Speciality Tags */}
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      Speciality Tags
                    </label>
                    {isEditMode ? (
                      <div>
                        <select
                          value=""
                          onChange={(e) => {
                            if (e.target.value && !editFormData.speciality_tags?.includes(e.target.value)) {
                              const currentTags = editFormData.speciality_tags || [];
                              handleEditFormChange('speciality_tags', [...currentTags, e.target.value]);
                            }
                          }}
                          style={{
                            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                            border: '2px solid #e2e8f0',
                            borderRadius: '12px',
                            padding: '14px 16px',
                            fontSize: '15px',
                            fontWeight: 500,
                            width: '100%',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="">Select specialities...</option>
                          {availableSpecialities.map((speciality, index) => (
                            <option key={`edit-speciality-${index}`} value={speciality}>
                              {speciality}
                            </option>
                          ))}
                        </select>
                        {editFormData.speciality_tags && editFormData.speciality_tags.length > 0 && (
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '8px',
                            marginTop: '12px'
                          }}>
                            {editFormData.speciality_tags.map((speciality, index) => (
                              <span key={`edit-selected-speciality-${index}-${speciality}`} style={{
                                background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                                color: 'white',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                fontSize: '13px',
                                fontWeight: 600,
                                boxShadow: '0 4px 12px rgba(43, 70, 99, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}>
                                {speciality}
                                <button 
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    padding: 0,
                                    margin: 0
                                  }}
                                  onClick={() => {
                                    const updatedTags = editFormData.speciality_tags.filter(s => s !== speciality);
                                    handleEditFormChange('speciality_tags', updatedTags);
                                  }}
                                  title="Remove"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{
                        background: 'white',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        minHeight: '50px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                        alignItems: 'flex-start'
                      }}>
                        {selectedResource.specialityTags && selectedResource.specialityTags.length > 0 ? (
                          selectedResource.specialityTags.map((speciality, index) => (
                            <span key={`details-speciality-${index}-${speciality}`} style={{
                              background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                              color: 'white',
                              padding: '6px 12px',
                              borderRadius: '8px',
                              fontSize: '13px',
                              fontWeight: 600,
                              boxShadow: '0 4px 12px rgba(43, 70, 99, 0.3)'
                            }}>
                              {speciality}
                            </span>
                          ))
                        ) : (
                          <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '14px' }}>
                            No speciality tags
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Institution Tags */}
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      Institution Tags
                    </label>
                    {isEditMode ? (
                      <div>
                        <select
                          value=""
                          onChange={(e) => {
                            if (e.target.value && !editFormData.institution_tags?.includes(e.target.value)) {
                              const currentTags = editFormData.institution_tags || [];
                              handleEditFormChange('institution_tags', [...currentTags, e.target.value]);
                            }
                          }}
                          style={{
                            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                            border: '2px solid #e2e8f0',
                            borderRadius: '12px',
                            padding: '14px 16px',
                            fontSize: '15px',
                            fontWeight: 500,
                            width: '100%',
                            outline: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="">Select institutions...</option>
                          {availableInstitutions.map((institution, index) => (
                            <option key={`edit-institution-${index}`} value={institution}>
                              {institution}
                            </option>
                          ))}
                        </select>
                        {editFormData.institution_tags && editFormData.institution_tags.length > 0 && (
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '8px',
                            marginTop: '12px'
                          }}>
                            {editFormData.institution_tags.map((institution, index) => (
                              <span key={`edit-selected-institution-${index}-${institution}`} style={{
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                color: 'white',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                fontSize: '13px',
                                fontWeight: 600,
                                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}>
                                {institution}
                                <button 
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    padding: 0,
                                    margin: 0
                                  }}
                                  onClick={() => {
                                    const updatedTags = editFormData.institution_tags.filter(i => i !== institution);
                                    handleEditFormChange('institution_tags', updatedTags);
                                  }}
                                  title="Remove"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{
                        background: 'white',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        minHeight: '50px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                        alignItems: 'flex-start'
                      }}>
                        {selectedResource.institutionTags && selectedResource.institutionTags.length > 0 ? (
                          selectedResource.institutionTags.map((institution, index) => (
                            <span key={`details-institution-${index}-${institution}`} style={{
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              color: 'white',
                              padding: '6px 12px',
                              borderRadius: '8px',
                              fontSize: '13px',
                              fontWeight: 600,
                              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                            }}>
                              {institution}
                            </span>
                          ))
                        ) : (
                          <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '14px' }}>
                            No institution tags
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* System & Uploader Information */}
              <div style={{
                background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                borderRadius: '16px',
                padding: '2rem',
                border: '2px solid rgba(245, 158, 11, 0.15)'
              }}>
                <h3 style={{ 
                  margin: '0 0 1.5rem 0', 
                  color: '#1e293b', 
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  System Information
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      Resource ID
                    </label>
                    <div style={{
                      background: 'white',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      padding: '14px 16px',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#6b7280',
                      fontFamily: 'ui-monospace, monospace',
                      wordBreak: 'break-all'
                    }}>
                      {selectedResource._id || 'N/A'}
                    </div>
                  </div>
                  
                  {selectedResource.file_id && (
                    <div>
                      <label style={{ 
                        fontWeight: 600, 
                        color: '#1e293b', 
                        display: 'block', 
                        marginBottom: '8px',
                        fontSize: '15px'
                      }}>
                        File ID
                      </label>
                      <div style={{
                        background: 'white',
                        border: '2px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#6b7280',
                        fontFamily: 'ui-monospace, monospace',
                        wordBreak: 'break-all'
                      }}>
                        {selectedResource.file_id}
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label style={{ 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      display: 'block', 
                      marginBottom: '8px',
                      fontSize: '15px'
                    }}>
                      Uploader Role
                    </label>
                    <div style={{
                      background: 'white',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      padding: '14px 16px',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#374151'
                    }}>
                      {getUploaderRoleDisplay(selectedResource)}
                    </div>
                  </div>
                  
                  {loadingUploaderDetails && (
                    <div style={{ 
                      gridColumn: '1 / -1', 
                      color: '#64748b', 
                      fontStyle: 'italic', 
                      textAlign: 'center',
                      padding: '1rem'
                    }}>
                      Loading additional uploader details...
                    </div>
                  )}
                  
                  {uploaderDetails && (
                    <>
                      {uploaderDetails.email && (
                        <div>
                          <label style={{ 
                            fontWeight: 600, 
                            color: '#1e293b', 
                            display: 'block', 
                            marginBottom: '8px',
                            fontSize: '15px'
                          }}>
                            Uploader Email
                          </label>
                          <div style={{
                            background: 'white',
                            border: '2px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '14px 16px',
                            fontSize: '15px',
                            fontWeight: 500,
                            color: '#374151'
                          }}>
                            {uploaderDetails.email}
                          </div>
                        </div>
                      )}
                      
                      {uploaderDetails.specialityId && (
                        <div>
                          <label style={{ 
                            fontWeight: 600, 
                            color: '#1e293b', 
                            display: 'block', 
                            marginBottom: '8px',
                            fontSize: '15px'
                          }}>
                            Department
                          </label>
                          <div style={{
                            background: 'white',
                            border: '2px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '14px 16px',
                            fontSize: '15px',
                            fontWeight: 500,
                            color: '#374151'
                          }}>
                            {uploaderDetails.specialityId}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ 
                display: 'flex', 
                gap: '1rem', 
                paddingTop: '2rem',
                borderTop: '2px solid #f1f5f9',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                {isEditMode ? (
                  <>
                    {/* Save Button */}
                    <button 
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '14px 24px',
                        borderRadius: '12px',
                        fontSize: '15px',
                        fontWeight: 600,
                        cursor: isUpdating ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                        transition: 'all 0.3s ease',
                        opacity: isUpdating ? 0.7 : 1
                      }}
                      onClick={handleSaveChanges}
                      disabled={isUpdating}
                      onMouseEnter={(e) => {
                        if (!isUpdating) {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.4)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isUpdating) {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                        }
                      }}
                    >
                      {isUpdating ? 'Saving...' : 'Save Changes'}
                    </button>
                    
                    {/* Cancel Button */}
                    <button 
                      style={{
                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '14px 24px',
                        borderRadius: '12px',
                        fontSize: '15px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => toggleEditMode(false)}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                      }}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    {selectedResource.file_id && (
                      <button 
                        style={{
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          color: 'white',
                          border: 'none',
                          padding: '14px 24px',
                          borderRadius: '12px',
                          fontSize: '15px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                          transition: 'all 0.3s ease'
                        }}
                        onClick={() => handleDownload(selectedResource)}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                        }}
                      >
                        Download File
                      </button>
                    )}
                    
                    {selectedResource.url && (
                      <button 
                        style={{
                          background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                          color: 'white',
                          border: 'none',
                          padding: '14px 24px',
                          borderRadius: '12px',
                          fontSize: '15px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          boxShadow: '0 4px 12px rgba(43, 70, 99, 0.3)',
                          transition: 'all 0.3s ease'
                        }}
                        onClick={() => window.open(selectedResource.url, '_blank', 'noopener,noreferrer')}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 6px 20px rgba(43, 70, 99, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 4px 12px rgba(43, 70, 99, 0.3)';
                        }}
                      >
                        Visit URL
                      </button>
                    )}
                  </>
                )}
                
                <button 
                  style={{
                    background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '14px 24px',
                    borderRadius: '12px',
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(107, 114, 128, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => setShowMetadataModal(false)}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(107, 114, 128, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(107, 114, 128, 0.3)';
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', 
        borderRadius: 20, 
        boxShadow: '0 8px 32px rgba(43, 70, 99, 0.1)', 
        padding: 32, 
        marginBottom: 32,
        border: '1px solid rgba(43, 70, 99, 0.1)'
      }}>
        <h3 style={{ 
          color: '#2b4663', 
          fontWeight: 700, 
          marginTop: 0, 
          marginBottom: 24,
          fontSize: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
                  Filters & Search
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
          <div>
            <label style={{ 
              fontWeight: 600, 
              color: '#2b4663', 
              display: 'block', 
              marginBottom: 12,
              fontSize: 15
            }}>
              View as:
            </label>
            <select 
              value={viewAs} 
              onChange={e => { 
                setViewAs(e.target.value); 
                setViewRank("ALL"); 
              }} 
              style={{
                ...modernInput,
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '2px solid #e2e8f0',
                borderRadius: 12,
                padding: '14px 16px',
                fontSize: 15,
                fontWeight: 500
              }}
            >
              <option value="ALL">All Resources</option>
              <option value="student">Student</option>
              <option value="preceptor">Preceptor</option>
            </select>
          </div>
          
          {viewAs !== "ALL" && (
            <div>
              <label style={{ 
                fontWeight: 600, 
                color: '#2b4663', 
                display: 'block', 
                marginBottom: 12,
                fontSize: 15
              }}>
                Rank:
              </label>
              <select 
                value={viewRank} 
                onChange={e => setViewRank(e.target.value)} 
                style={{
                  ...modernInput,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: '2px solid #e2e8f0',
                  borderRadius: 12,
                  padding: '14px 16px',
                  fontSize: 15,
                  fontWeight: 500
                }}
              >
                <option value="ALL">All Ranks</option>
                {availableRanks.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label style={{ 
              fontWeight: 600, 
              color: '#2b4663', 
              display: 'block', 
              marginBottom: 12,
              fontSize: 15
            }}>
              Speciality Tags:
            </label>
            <select 
              value="" 
              onChange={handleSpecialityChange} 
              style={{
                ...modernInput,
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '2px solid #e2e8f0',
                borderRadius: 12,
                padding: '14px 16px',
                fontSize: 15,
                fontWeight: 500
              }}
              disabled={loadingOptions}
            >
              <option value="">Select specialities...</option>
              {availableSpecialities.map((speciality, index) => (
                <option key={`speciality-${index}-${speciality}`} value={speciality}>
                  {speciality}
                </option>
              ))}
            </select>
            {selectedSpecialities.length > 0 && (
              <div style={{...selectedTagsStyle, marginTop: 12}}>
                {selectedSpecialities.map((speciality, index) => (
                  <span key={`selected-speciality-${index}-${speciality}`} style={{
                    ...tagStyle,
                    background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                    boxShadow: '0 4px 12px rgba(43, 70, 99, 0.3)',
                    fontSize: 13,
                    padding: '6px 12px'
                  }}>
                    {speciality}
                    <button 
                      style={removeTagButton}
                      onClick={() => removeInstitution(institution)}
                      title="Remove"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label style={{ 
              fontWeight: 600, 
              color: '#2b4663', 
              display: 'block', 
              marginBottom: 12,
              fontSize: 15
            }}>
              Institution Tags:
            </label>
            <select 
              value="" 
              onChange={handleInstitutionChange} 
              style={{
                ...modernInput,
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '2px solid #e2e8f0',
                borderRadius: 12,
                padding: '14px 16px',
                fontSize: 15,
                fontWeight: 500
              }}
              disabled={loadingOptions}
            >
              <option value="">Select institutions...</option>
              {availableInstitutions.map((institution, index) => (
                <option key={`institution-${index}-${institution}`} value={institution}>
                  {institution}
                </option>
              ))}
            </select>
            {selectedInstitutions.length > 0 && (
              <div style={{...selectedTagsStyle, marginTop: 12}}>
                {selectedInstitutions.map((institution, index) => (
                  <span key={`selected-institution-${index}-${institution}`} style={{
                    ...tagStyle,
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                    fontSize: 13,
                    padding: '6px 12px'
                  }}>
                    {institution}
                    <button 
                      style={removeTagButton}
                      onClick={() => removeSpeciality(speciality)}
                      title="Remove"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            padding: '20px',
            background: 'linear-gradient(135deg, #f0f8ff 0%, #e0f2fe 100%)',
            borderRadius: 16,
            border: '2px solid rgba(59, 130, 246, 0.1)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}>
              <input 
                type="checkbox" 
                id="include-general-resources"
                checked={includeGeneralResources} 
                onChange={e => setIncludeGeneralResources(e.target.checked)}
                style={includeGeneralResources ? modernCheckboxChecked : modernCheckbox}
                className="modern-checkbox"
              />
              <label 
                htmlFor="include-general-resources"
                style={{
                  fontWeight: 600,
                  color: '#2b4663',
                  fontSize: 15,
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                Include General Resources
              </label>
              <button 
                type="button"
                style={{
                  background: 'rgba(59, 130, 246, 0.1)',
                  color: '#3b82f6',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
                title="Include general resources means ones with no speciality nor institution tags - they are available to everyone"
              >
                i
              </button>
            </div>
            <div style={{
              fontSize: 13,
              color: '#64748b',
              fontStyle: 'italic',
              lineHeight: 1.4
            }}>
              General resources have no speciality or institution tags and are available to everyone
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, marginTop: 32, justifyContent: 'center' }}>
          <button 
            onClick={handleReload} 
            style={{
              ...modernButton,
              background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
              padding: '14px 32px',
              fontSize: 16,
              fontWeight: 600,
              borderRadius: 12,
              boxShadow: '0 6px 20px rgba(43, 70, 99, 0.3)'
            }}
          >
            Apply Filters
          </button>
          <button 
            onClick={clearFilters} 
            style={{
              ...modernButtonSecondary,
              padding: '14px 32px',
              fontSize: 16,
              fontWeight: 600,
              borderRadius: 12
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Resource List */}
      <div style={{ 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', 
        borderRadius: 20, 
        boxShadow: '0 8px 32px rgba(43, 70, 99, 0.1)', 
        padding: 32, 
        marginBottom: 32,
        border: '1px solid rgba(43, 70, 99, 0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 32
        }}>
          <h3 style={{ 
            color: '#2b4663', 
            fontWeight: 700, 
            margin: 0,
            fontSize: 24,
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            Resource Library
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="column-selector-container" ref={columnSelectorRef}>
              <button
                type="button"
                className="column-selector-button"
                onClick={() => setShowColumnSelector((prev) => !prev)}
              >
                Customize Columns
              </button>
              {showColumnSelector && (
                <div className="column-selector-dropdown">
                  <div className="column-selector-header">
                    <h4>Visible Columns</h4>
                  </div>
                  {RESOURCE_COLUMNS.map((col) => (
                    <label key={col.key} className="column-option">
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(col.key)}
                        disabled={visibleColumns.length === 1 && visibleColumns.includes(col.key)}
                        onChange={() => {
                          setVisibleColumns((prev) =>
                            prev.includes(col.key)
                              ? prev.filter((k) => k !== col.key)
                              : [...prev, col.key]
                          );
                        }}
                      />
                      <span>{col.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <ExportButton 
              onExport={handleExport}
              formats={['PDF', 'CSV']}
              disabled={loading || resources.length === 0}
              customLabel="Export Resources"
            />
          </div>
        </div>
        {loading ? (
          <div style={{ padding: '40px 0' }}>
            <LoadingSpinner text="Loading resources..." variant="inline" />
          </div>
        ) : error ? (
          <div style={{ 
            color: '#ef4444', 
            background: '#fee2e2', 
            padding: 24, 
            borderRadius: 12, 
            textAlign: 'center',
            fontSize: 16,
            fontWeight: 500
          }}>
            {error}
          </div>
        ) : (
          <>
            <div style={{ width: '100%', overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'separate', 
                borderSpacing: 0, 
                marginBottom: 24, 
                borderRadius: 16, 
                overflow: 'hidden', 
                background: '#fff', 
                tableLayout: 'auto',
                boxShadow: '0 8px 32px rgba(43, 70, 99, 0.1)',
                border: '1px solid rgba(43, 70, 99, 0.1)',
                minWidth: `${Math.max(visibleColumns.length * 180, 920)}px`
              }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(140deg, rgba(10, 30, 52, 0.96) 0%, rgba(21, 45, 72, 0.92) 100%)' }}>
                    {RESOURCE_COLUMNS.filter((col) => visibleColumns.includes(col.key)).map((col) => (
                      <th key={col.key} style={{ padding: 20, borderBottom: 'none', color: '#fff', fontWeight: 700, fontSize: 15, textAlign: 'left', minWidth: 160 }}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {resources.length === 0 ? (
                    <tr>
                      <td colSpan={visibleColumns.length} style={{ 
                        textAlign: 'center', 
                        padding: 60, 
                        color: '#64748b',
                        fontSize: 18,
                        fontWeight: 500
                      }}>
                        <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }}>📚</div>
                        No resources found.
                      </td>
                    </tr>
                  ) : resources.map((r, idx) => (
                    <tr key={idx} 
                        style={{ 
                          cursor: 'pointer', 
                          transition: 'all 0.3s ease',
                          background: idx % 2 === 0 ? '#fff' : '#f8fafc',
                          borderBottom: '1px solid #f1f5f9'
                        }} 
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%)';
                          e.currentTarget.style.transform = 'scale(1.005)';
                          e.currentTarget.style.boxShadow = '0 4px 20px rgba(43, 70, 99, 0.1)';
                        }} 
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#f8fafc';
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = 'none';
                        }} 
                        onClick={() => handleResourceClick(r)}
                    >
                      {RESOURCE_COLUMNS.filter((col) => visibleColumns.includes(col.key)).map((col) => (
                        <td key={col.key} style={{ padding: 16, minWidth: 160 }}>
                          {renderResourceCell(r, col.key)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              gap: 20, 
              marginTop: 24 
            }}>
              <button 
                onClick={handlePrev} 
                disabled={page === 1 || totalPages <= 1}
                style={{
                  ...modernButtonSecondary,
                  padding: '12px 24px',
                  fontSize: 14,
                  fontWeight: 600,
                  opacity: page === 1 || totalPages <= 1 ? 0.5 : 1,
                  cursor: page === 1 || totalPages <= 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Previous
              </button>
              <span style={{ 
                color: '#2b4663', 
                fontWeight: 600, 
                fontSize: 16,
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #f0f8ff 0%, #e0f2fe 100%)',
                borderRadius: 12,
                border: '1px solid rgba(43, 70, 99, 0.1)'
              }}>
                Page {totalPages === 0 ? 1 : page} of {totalPages === 0 ? 1 : totalPages}
              </span>
              <button 
                onClick={handleNext} 
                disabled={page === (totalPages === 0 ? 1 : totalPages) || totalPages <= 1}
                style={{
                  ...modernButtonSecondary,
                  padding: '12px 24px',
                  fontSize: 14,
                  fontWeight: 600,
                  opacity: page === (totalPages === 0 ? 1 : totalPages) || totalPages <= 1 ? 0.5 : 1,
                  cursor: page === (totalPages === 0 ? 1 : totalPages) || totalPages <= 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminResourceCenter; 
